import {
  Component,
  OnInit,
  Input,
  ChangeDetectorRef,
  inject,
  EventEmitter,
  Output,
} from '@angular/core';
import { RixtremaService } from '../rixtrema.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { RixtremaPlan } from '../../models/rixtrema-data';
import { saveAs } from '@progress/kendo-file-saver';
import { EnvironmentService } from '../../services/environment.service';
import { Platform } from '@angular/cdk/platform';
import { RightBridgeApiService } from '../../services/right-bridge-api.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { NgxLoadingModule } from 'ngx-loading';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatRadioModule } from '@angular/material/radio';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule, DecimalPipe, PercentPipe } from '@angular/common';

@Component({
  selector: 'app-rixtrema-dialog',
  templateUrl: './rixtrema-dialog.component.html',
  styleUrls: ['./rixtrema-dialog.component.scss'],
  imports: [
    NgxLoadingModule,
    MatIconModule,
    MatTooltipModule,
    MatRadioModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    PercentPipe,
    DecimalPipe,
    CommonModule,
  ],
})
export class RixtremaDialogComponent implements OnInit {
  private rixtremaSrc = inject(RixtremaService);
  private dialogRef =
    inject<MatDialogRef<RixtremaDialogComponent>>(MatDialogRef);
  private ref = inject(ChangeDetectorRef);
  private pltfrm = inject(Platform);
  private envSvc = inject(EnvironmentService);
  private rbs = inject(RightBridgeApiService);
  private ss = inject(SessionStorageService);

  private environment;
  public searchTerm = null;
  public zip = null;
  public distance = null;
  public sponsor = null;
  public selectedPlan = null;
  public selectedPlanData: RixtremaPlan | null = null;
  public selectedIndex;
  public showData = false;
  public showRlc = false;
  public rlcAccess = false;
  public showSubplanSelect = false;
  public resultsMessage = null;
  public plans: Array<RixtremaPlan> = [];
  public advanced = false;
  public loading = false;
  public loggedIn = false;
  public planType: string | null = null;
  public server: 'rixtrema' | 'rightbridge' | null = null;
  public buttonType = null;
  public serverName: 'Rixtrema' | 'RightBridge' | null = null;
  public subplanID: string | null = null;
  public baseUrl = null;
  public ein = null;
  public searchType = 'name';
  public zipName = null;
  public UnitInfo = null;
  public hasSearched = false;
  public fieldText =
    'Enter Employer Plan Name. End with `*` for wildcard search.';
  data;

  @Input() groupIndex;
  @Output() templateDataOut = new EventEmitter();

  constructor() {
    const data = inject(MAT_DIALOG_DATA);

    if (data.dataF5500) {
      this.server =
        data.fieldData.model.UnitRolloverSettings_ExternalSource === 1
          ? 'rixtrema'
          : 'rightbridge';
      let planType = 'DC';
      if (data.buttonType === 'searchPension') {
        planType = 'DB';
      }
      this.groupIndex = data.fieldData.parent.form.controls.indexOf(
        data.fieldData.form
      );
      this.planType = planType;
      this.serverName = this.server === 'rixtrema' ? 'Rixtrema' : 'RightBridge';
      this.planType = data['planType'] ? data['planType'] : 'DC';
      this.UnitInfo = this.ss.get('globals')?.GlobalsGroup?.UnitInfo || null;
      this.rixtremaSrc.setServer(this.server);
    } else {
      this.groupIndex = data?.groupIndex;
      this.planType = data?.planType;
      this.server = data?.server;
      this.buttonType = data?.buttonType;
      this.serverName = this.server === 'rixtrema' ? 'Rixtrema' : 'RightBridge';
      this.UnitInfo = data?.UnitInfo || null;
      this.rixtremaSrc.setServer(data?.server);
    }
    this.data = data;
  }

  ngOnInit() {
    this.environment = this.envSvc.get();
    this.baseUrl = this.environment.apiBase;
    const rights = this.rbs.getRights();
    rights.subscribe(val => {
      this.rlcAccess = val.includes('RLC');
      this.showRlc = this.rlcAccess;
    });
    this.startSession();
    const globals = this.ss.get('globals');
    if (
      globals?.GlobalsGroup?.UnitCustomText?.F5500LookupEmployerPlanNameLabel
    ) {
      this.fieldText =
        globals.GlobalsGroup.UnitCustomText.F5500LookupEmployerPlanNameLabel;
    }
  }

  startSession(counter = 0, callback = null) {
    if (counter > 5) {
      return;
    }
    this.rixtremaSrc.loginRixtrema().subscribe({
      next: () => {
        this.loggedIn = true;
        if (callback !== null) {
          callback(counter + 1);
        }
      },
      error: () => {
        this.resultsMessage = `Error logging in to ${this.serverName}`;
        this.loggedIn = false;
        this.rbs.clearJwt();
        this.startSession(counter + 1, callback);
      },
    });
  }

  search() {
    this.hasSearched = true;
    switch (this.searchType) {
      case 'name':
        this.basicSearch();
        break;
      case 'sponsor':
        this.advancedSearch();
        break;
      case 'ein':
        this.advancedSearch();
        break;
      case 'zip':
        this.advancedSearch();
        break;
      default:
        this.basicSearch();
        break;
    }
  }

  save() {
    if (this.selectedPlanData !== null) {
      this.selectedPlanData['groupIndex'] = this.groupIndex;
      this.selectedPlanData['server'] = this.server;
      this.selectedPlanData['buttonType'] = this.buttonType;
      this.selectedPlanData['subplanID'] = this.subplanID;
      if (this.data.dataF5500) {
        this.selectedPlanData['index'] = +this.data.index;
        this.selectedPlanData['group'] = this.data.group;
        this.selectedPlanData['varset'] = this.data.varset;
        this.templateDataOut.emit(this.selectedPlanData);
      } else {
        this.rixtremaSrc.planData(this.selectedPlanData);
        this.close();
      }
    }
  }

  close() {
    this.dialogRef.close();
    this.dialogRef.afterClosed().subscribe(() => {
      this.dialogRef = null;
    });
  }

  getPdf() {
    const iosSafari =
      this.pltfrm.IOS ||
      (navigator?.['userAgentData']?.platform === 'macOS' &&
        navigator.maxTouchPoints > 1);
    this.loading = true;

    this.rbs.getRlcPdf(this.subplanID).subscribe({
      next: data => {
        const filename = `${this.subplanID}`;
        this.loading = false;

        let fileData;
        const reader = new FileReader();
        reader.readAsDataURL(data);
        reader.onloadend = () => {
          fileData = reader.result;
          saveAs(fileData, filename, {
            proxyURL: `${this.baseUrl}/util/proxy`,
            proxyTarget: '_self',
            forceProxy: iosSafari,
          });
        };
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  getFactsheet() {
    const iosSafari =
      this.pltfrm.IOS ||
      (navigator?.['userAgentData']?.platform === 'macOS' &&
        navigator.maxTouchPoints > 1);
    this.loading = true;
    if (this.selectedPlanData === null) {
      this.loading = false;
      return;
    }
    const ackID = this.selectedPlanData.ACK_ID;

    this.rbs.getFactsheet(ackID).subscribe({
      next: data => {
        const filename = `F5500_Factsheet_${ackID}.pdf`;
        this.loading = false;

        let fileData;
        const reader = new FileReader();
        reader.readAsDataURL(data);
        reader.onloadend = () => {
          fileData = reader.result;
          saveAs(fileData, filename, {
            proxyURL: `${this.baseUrl}/util/proxy`,
            proxyTarget: '_self',
            forceProxy: iosSafari,
          });
          // this.loading = false;
        };
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  basicSearch(count = 0) {
    this.loading = true;
    this.planSelect(null);
    this.resultsMessage = '';
    this.rixtremaSrc
      .IncrementalSearch(30, this.searchTerm, this.planType, this.rlcAccess)
      .subscribe({
        next: data => {
          this.loading = false;
          this.updateList(data);
        },
        error: () => {
          if (count < 2) {
            this.rbs.clearJwt();
            this.startSession(count, count => {
              this.basicSearch(count);
            });
          } else {
            this.loading = false;
            this.plans = null;
            this.resultsMessage = `Error Performing Search`;
          }
        },
      });
  }

  advancedSearch(count = 0) {
    this.loading = true;
    this.planSelect(null);
    this.loading = true;
    this.resultsMessage = '';

    this.distance = !this.distance ? '0' : this.distance;
    this.sponsor = this.searchType === 'zip' ? this.zipName : this.sponsor;

    this.rixtremaSrc
      .AdvancedSearch(
        this.zip,
        this.distance,
        this.sponsor,
        this.planType,
        this.ein,
        null,
        this.rlcAccess
      )
      .subscribe({
        next: data => {
          this.loading = false;
          this.updateList(data);
        },
        error: () => {
          if (count < 2) {
            this.rbs.clearJwt();
            this.startSession(count, count => {
              this.advancedSearch(count);
            });
          } else {
            this.loading = false;
            this.plans = null;
            this.resultsMessage = `Error Performing Search`;
          }
        },
      });
  }

  searchValid() {
    if (this.loading || !this.loggedIn) {
      return false;
    }
    return (
      (this.searchTerm && this.searchTerm.length > 0) ||
      (this.ein && this.ein.length > 0) ||
      (this.zip && this.zip.length > 0) ||
      (this.distance && this.distance.length > 0) ||
      (this.sponsor && this.sponsor.length > 0)
    );
  }

  updateList(data) {
    this.loading = false;
    if (!data.Plan || data.Plan.length === 0) {
      this.resultsMessage = 'No Plans Found';
      this.plans = null;
    } else {
      this.resultsMessage = '';
      this.plans = data.Plan;
    }
  }

  toggleAdvanced() {
    this.advanced = !this.advanced;
    this.plans = null;
    this.resultsMessage = '';
    this.planSelect(null);
  }

  noneFound() {
    this.rixtremaSrc.planData({
      groupIndex: this.groupIndex,
      server: this.server,
      buttonType: this.buttonType,
    });
    this.close();
  }

  planSelect(event, count = 0) {
    this.selectedPlanData = null;
    this.selectedPlan = event;
    if (event !== null) {
      this.loading = true;
      this.rixtremaSrc
        .GetPlans(this.selectedPlan!['ACK_ID'], this.rlcAccess)
        .subscribe({
          next: data => {
            if (data && data?.Plan && data.Plan.length > 0) {
              data.Plan[0]['groupIndex'] = this.groupIndex;
              this.selectedPlanData = data.Plan[0];
              this.subplanID = '';
              this.showSubplanSelect = false;
              if (this.selectedPlanData.pdfs.length > 1) {
                this.showSubplanSelect = true;
              }
              if (this.selectedPlanData.pdfs.length === 1) {
                this.subplanID = this.selectedPlanData.pdfs[0].PathFilename;
              }
              this.showData = this.selectedPlan !== null;
              this.showRlc =
                this.selectedPlanData.pdfs.length > 0 && this.rlcAccess;
              this.ref.markForCheck();
            }
            this.loading = false;
          },
          error: () => {
            if (count < 2) {
              this.rbs.clearJwt();
              this.startSession(count, count => {
                this.planSelect(event, count);
              });
            } else {
              this.plans = null;
              this.resultsMessage = `Error Loading Plan Data`;
            }
            this.loading = false;
          },
        });
    } else {
      this.selectedPlanData = null;
      this.showData = false;
      this.subplanID = '';
    }
    this.ref.markForCheck();
  }

  clearData() {
    this.searchTerm = '';
    this.zip = '';
    this.sponsor = '';
    this.zipName = '';
    this.distance = '';
    this.ein = '';

    this.plans = null;
  }

  checkICantFindItDisabled(): boolean {
    if (this.UnitInfo?.ForceF5500Lookup && !this.hasSearched) {
      return true;
    }
    return false;
  }
}
